import { useState, type ReactNode } from 'react';
import StoreBadges from './StoreBadges';

const DISMISS_KEY = 'flock_mobile_gate_dismissed';

/**
 * True for phones and tablets. UA-based (not viewport width) so a narrow
 * desktop window is never gated. iPadOS 13+ reports as Macintosh, so a
 * touch-capable "Mac" counts as an iPad.
 */
function isMobileDevice(): boolean {
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile/i.test(ua)) {
    return true;
  }
  return /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

function loadDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * my.flock.church is a desktop experience — on phones/tablets, show a
 * branded interstitial pointing to the mobile app (with the same store
 * badges as the flock.church download page) instead of mounting the app.
 * "Continue anyway" dismisses for the browser session.
 */
export default function MobileGate({ children }: { children: ReactNode }) {
  const [isMobile] = useState(isMobileDevice);
  const [dismissed, setDismissed] = useState(loadDismissed);

  if (!isMobile || dismissed) return <>{children}</>;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Ignore storage errors
    }
    setDismissed(true);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-flock-navy-900 px-6 py-10 text-center">
      <img src="/favicon.png" alt="Flock" className="mb-4 h-14 w-14" />
      <h1 className="text-xl font-bold text-white">Flock is better in the app</h1>
      <p className="mt-3 max-w-sm text-sm leading-relaxed text-flock-navy-200">
        my.flock.church is designed for desktop — a Mac or Windows PC. On your phone or tablet,
        the Flock app is the best way to stay connected with your group.
      </p>
      <div className="mt-6">
        <StoreBadges />
      </div>
      <p className="mt-8 max-w-sm text-xs text-flock-navy-400">
        Already have the app? Open Flock from your home screen.
      </p>
      <button
        onClick={dismiss}
        className="mt-2 text-xs text-flock-navy-400 underline underline-offset-2 transition hover:text-flock-navy-200"
      >
        Continue to the web app anyway
      </button>
    </div>
  );
}
